import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Truck,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  Package,
  IndianRupee,
  Calendar,
  Briefcase,
  Clock,
  CheckCircle2,
  Wallet,
  Receipt,
  FileText,
  User,
  Edit2
} from 'lucide-react';
import { formatMoney } from '@vns/core';
import { useSupplierDetail, type SupplierRow } from '../lib/directory';
import Shell from '../components/Shell';
import { SupplierDrawer } from './Suppliers';
import {
  StatusChip,
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  initials,
} from '../components/ui';

type Tab = 'purchases' | 'payments' | 'projects';
const TABS: Array<{ key: Tab; label: string; icon: typeof Truck }> = [
  { key: 'purchases', label: 'Purchase History', icon: Package },
  { key: 'payments', label: 'Payments', icon: Wallet },
  { key: 'projects', label: 'Supplied Projects', icon: Briefcase },
];

export default function SupplierDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('purchases');
  const [editing, setEditing] = useState(false);
  const { data, isLoading, error, refetch } = useSupplierDetail(id);

  // Convert SupplierDetail to SupplierRow for the edit drawer
  const supplierRow: SupplierRow | undefined = data
    ? {
        id: data.id,
        name: data.name,
        contact_person: data.contactPerson,
        phone: data.phone,
        city: data.city,
        gst_number: data.gstNumber,
        materialTypes: data.materialTypes,
      }
    : undefined;

  return (
    <Shell
      title={data?.name ?? 'Supplier Profile'}
      crumb="Suppliers"
      actions={
        data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-auto btn-sm"
              onClick={() => setEditing(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
            >
              <Edit2 size={13} />
              <span>Edit Profile</span>
            </button>
          </div>
        )
      }
    >
      <button
        className="btn btn-auto btn-sm btn-ghost"
        style={{
          marginBottom: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--text-soft)',
          paddingLeft: 4,
        }}
        onClick={() => navigate('/suppliers')}
      >
        <ArrowLeft size={14} />
        <span>Back to Suppliers</span>
      </button>

      {isLoading ? (
        <div className="card"><TableSkeleton rows={4} cols={4} /></div>
      ) : error ? (
        <div className="card"><ErrorState error={error} onRetry={() => void refetch()} /></div>
      ) : !data ? (
        <div className="card"><EmptyState icon="truck" title="Supplier not found" /></div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Supplier Identity Card */}
          <div
            className="card"
            style={{
              padding: '22px 24px',
              marginBottom: 20,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              boxShadow: 'var(--shadow-xs)',
            }}
          >
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div
                className="avatar-sq"
                style={{
                  width: 56,
                  height: 56,
                  fontSize: 18,
                  borderRadius: 'var(--radius-sm)',
                  background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                  color: '#2563eb',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.12)',
                  border: '1px solid #bfdbfe',
                  flexShrink: 0,
                }}
              >
                {initials(data.name)}
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: 'var(--text)' }}>
                    {data.name}
                  </h2>
                  <span
                    className="chip chip-blue"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-xs)',
                    }}
                  >
                    <Truck size={11} />
                    <span>Material Supplier</span>
                  </span>
                </div>

                {/* Primary Contact & Metadata Badges */}
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginTop: 10,
                    fontSize: 13,
                    color: 'var(--text-soft)',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Phone size={13} style={{ color: 'var(--text-faint)' }} />
                    <span className="num" style={{ fontWeight: 500 }}>{data.phone}</span>
                  </span>

                  {data.city && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <MapPin size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>{data.city}</span>
                    </span>
                  )}

                  {data.contactPerson && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                       <User size={13} style={{ color: 'var(--text-faint)' }} />
                      <span>Contact Person: <strong style={{ color: 'var(--text)', fontWeight: 600 }}>{data.contactPerson}</strong></span>
                    </span>
                  )}

                  {data.gstNumber && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        background: 'var(--surface-2)',
                        padding: '2px 7px',
                        borderRadius: 'var(--radius-xs)',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <CreditCard size={12} style={{ color: 'var(--text-faint)' }} />
                      <span>GST: <strong>{data.gstNumber}</strong></span>
                    </span>
                  )}
                </div>

                {/* Materials Supplied Tags */}
                {data.materialTypes.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Supplies:
                    </span>
                    {data.materialTypes.map((t) => (
                      <span
                        key={t}
                        style={{
                          display: 'inline-block',
                          padding: '3px 9px',
                          borderRadius: 'var(--radius-xs)',
                          fontSize: 12,
                          fontWeight: 500,
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          border: '1px solid #bfdbfe',
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Supplier Financials */}
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div className="lbl">
                <span>Total Purchases</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Package size={14} />
                </div>
              </div>
              <div className="val">{formatMoney(data.totalPurchasePaise)}</div>
              <div className="sub">cumulative material orders</div>
            </div>

            <div className="kpi">
              <div className="lbl">
                <span>Total Amount Paid</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 size={14} />
                </div>
              </div>
              <div className="val" style={{ color: '#059669' }}>
                {formatMoney(data.totalPaidPaise)}
              </div>
              <div className="sub">settled vendor invoices</div>
            </div>

            <div className="kpi">
              <div className="lbl">
                <span>Outstanding Dues</span>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 'var(--radius-xs)',
                    background: data.pendingPaise > 0 ? '#fff7ed' : '#f8fafc',
                    color: data.pendingPaise > 0 ? '#ea580c' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IndianRupee size={14} />
                </div>
              </div>
              <div
                className="val"
                style={{ color: data.pendingPaise > 0 ? '#ea580c' : 'var(--text-faint)' }}
              >
                {formatMoney(data.pendingPaise)}
              </div>
              <div className="sub">
                {data.pendingPaise > 0 ? 'balance payment pending' : 'all accounts settled'}
              </div>
            </div>

            <div className="kpi">
              <div className="lbl">
                <span>Bank on File</span>
                <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f5f3ff', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CreditCard size={14} />
                </div>
              </div>
              <div className="val" style={{ fontSize: 15, fontWeight: 600 }}>
                {data.bankName ?? '—'}
              </div>
              <div className="sub">for RTGS / NEFT transfers</div>
            </div>
          </div>

          {/* Tabbed Ledgers Card */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              style={{
                padding: '0 16px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
                overflowX: 'auto',
              }}
            >
              <div style={{ display: 'flex', gap: 18 }}>
                {TABS.map((t) => {
                  const TabIcon = t.icon;
                  const active = tab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      style={{
                        padding: '12px 2px',
                        fontSize: 13,
                        fontWeight: active ? 600 : 500,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                        background: 'none',
                        border: 'none',
                        whiteSpace: 'nowrap',
                        color: active ? 'var(--accent)' : 'var(--text-faint)',
                        borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <TabIcon size={14} />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="table-wrap">
              {tab === 'purchases' &&
                (!data.purchases.length ? (
                  <EmptyState icon="box" title="No purchases recorded yet" />
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', paddingLeft: 24 }}>Date</th>
                        <th style={{ textAlign: 'left' }}>Project</th>
                        <th style={{ textAlign: 'left' }}>Material</th>
                        <th style={{ textAlign: 'left' }}>Quantity</th>
                        <th style={{ textAlign: 'left' }}>Rate</th>
                        <th style={{ textAlign: 'right', paddingRight: 24 }}>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.purchases.map((p) => (
                        <tr
                          key={p.id}
                          className="clickable"
                          onClick={() => navigate(`/projects/${p.projectId}`)}
                        >
                          <td style={{ textAlign: 'left', paddingLeft: 24 }}>{formatDate(p.date)}</td>
                          <td className="name" style={{ textAlign: 'left' }}>{p.projectName}</td>
                          <td style={{ textAlign: 'left' }}>{p.material}</td>
                          <td className="num" style={{ textAlign: 'left', fontWeight: 600 }}>
                            {p.quantity.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                            {p.unit ? ` ${p.unit}` : ''}
                          </td>
                          <td className="num" style={{ textAlign: 'left' }}>
                            {formatMoney(p.ratePaise)}
                          </td>
                          <td className="num" style={{ textAlign: 'right', paddingRight: 24, fontWeight: 600 }}>
                            {formatMoney(p.amountPaise)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'left', paddingLeft: 24, fontWeight: 700 }}>
                          Total Purchases
                        </td>
                        <td className="num" style={{ textAlign: 'right', paddingRight: 24, fontWeight: 700 }}>
                          {formatMoney(data.purchases.reduce((s, p) => s + p.amountPaise, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ))}

              {tab === 'payments' &&
                (!data.payments.length ? (
                  <EmptyState icon="wallet" title="No payments recorded yet" />
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', paddingLeft: 24 }}>Date</th>
                        <th style={{ textAlign: 'left' }}>Project</th>
                        <th style={{ textAlign: 'left' }}>Amount Paid</th>
                        <th style={{ textAlign: 'left' }}>Payment Mode</th>
                        <th style={{ textAlign: 'right', paddingRight: 24 }}>Balance Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((p) => (
                        <tr key={p.id}>
                          <td style={{ textAlign: 'left', paddingLeft: 24 }}>{formatDate(p.date)}</td>
                          <td style={{ textAlign: 'left' }}>{p.projectName ?? '—'}</td>
                          <td className="num" style={{ textAlign: 'left', color: '#059669', fontWeight: 600 }}>
                            {formatMoney(p.amountPaise)}
                          </td>
                          <td style={{ textAlign: 'left', textTransform: 'uppercase', fontSize: 12 }}>
                            <span style={{ background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border-subtle)' }}>
                              {p.mode}
                            </span>
                          </td>
                          <td
                            className="num"
                            style={{
                              textAlign: 'right',
                              paddingRight: 24,
                              color: p.balancePaise > 0 ? '#ea580c' : 'var(--text-faint)',
                              fontWeight: p.balancePaise > 0 ? 600 : 400,
                            }}
                          >
                            {formatMoney(p.balancePaise)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ textAlign: 'left', paddingLeft: 24, fontWeight: 700 }}>
                          Total Paid Out
                        </td>
                        <td className="num" style={{ textAlign: 'left', fontWeight: 700, color: '#059669' }}>
                          {formatMoney(data.payments.reduce((s, p) => s + p.amountPaise, 0))}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  </table>
                ))}

              {tab === 'projects' &&
                (!data.projects.length ? (
                  <EmptyState icon="briefcase" title="No projects supplied to yet" />
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Project Name</th>
                        <th style={{ textAlign: 'center' }}>Client</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projects.map((p) => (
                        <tr
                          key={p.id}
                          className="clickable"
                          onClick={() => navigate(`/projects/${p.id}`)}
                        >
                          <td className="name" style={{ textAlign: 'center' }}>{p.name}</td>
                          <td style={{ textAlign: 'center' }}>{p.clientName}</td>
                          <td style={{ textAlign: 'center' }}>
                            <StatusChip value={p.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
            </div>
          </div>
        </motion.div>
      )}

      {editing && supplierRow && (
        <SupplierDrawer
          existing={supplierRow}
          onClose={() => {
            setEditing(false);
            void refetch();
          }}
        />
      )}
    </Shell>
  );
}

